import { useMemo } from 'react';
import { useEntityStore } from '@/lib/sync/entity-store';
import { useTranscript } from './use-transcript';
import { useEntityAttendees } from './use-entity-attendees';
import { resolveAttendeePhotoUrl } from './resolve-attendee-photo';
import { buildAttendeeNameMap } from './attendee-name-map';
import { TranscriptView } from './transcript-view';
import type { TranscriptSegmentData } from './parse-transcript';
import type { SpeakerLabel } from './types/types';

/** Stable empty-labels reference so the store selector doesn't return a new
 * object each render (which would cause an infinite re-render loop). */
const EMPTY_LABELS: Record<string, SpeakerLabel> = {};

export interface TranscriptReaderProps {
  recordingId: string;
  /** The entity the attendees live on (the recording's notes entity). The
   * attendees endpoint is keyed by (entity, surface), not recording. */
  entityId?: string | null;
  /** This recording's audio surface. Scopes the attendee roster to this
   * surface's own linked event + manual rows; omitted reproduces the
   * document-level roster. */
  surfaceId?: string | null;
  className?: string;
}

/**
 * Authenticated-app transcript reader. Gathers live data — the transcript blob
 * (HTTP), editable speaker labels + calendar event (entity store), and live
 * attendees (merged endpoint) — then hands it to the presentational
 * `TranscriptView`. The public share path uses `TranscriptView` directly with
 * server-fetched data instead (see SurfaceShareTranscript), so all rendering
 * lives in one place.
 */
export function TranscriptReader({
  recordingId,
  entityId: entityIdProp,
  surfaceId,
  className,
}: TranscriptReaderProps) {
  const { data, loading } = useTranscript(recordingId);
  const segments: TranscriptSegmentData[] = useMemo(() => data?.segments ?? [], [data]);
  const calendarEventId = useEntityStore((s) => s.recordings.get(recordingId)?.calendarEventId ?? null);
  const speakerLabels = useEntityStore((s) => s.recordings.get(recordingId)?.speakerLabels ?? EMPTY_LABELS);
  // The attendees endpoint is keyed by ENTITY id (the recording's notes
  // entity). Prefer the explicit entity from the consumer; fall back to the
  // recording's stored entityId.
  const recEntityId = useEntityStore((s) => s.recordings.get(recordingId)?.entityId ?? null);
  const entityId = entityIdProp ?? recEntityId;
  const { suggestions, loading: attendeesLoading } = useEntityAttendees(entityId, surfaceId);
  const attendeeNameByEmail = useMemo(() => buildAttendeeNameMap(suggestions), [suggestions]);
  const attendeePhotoByEmail = useMemo(() => {
    const map: Record<string, string> = {};
    for (const a of suggestions) {
      const photo = resolveAttendeePhotoUrl(a);
      if (photo) map[a.email.toLowerCase()] = photo;
    }
    return map;
  }, [suggestions]);

  return (
    <TranscriptView
      recordingId={recordingId}
      segments={segments}
      loading={loading}
      speakerLabels={speakerLabels}
      calendarEventId={calendarEventId}
      entityId={entityId}
      surfaceId={surfaceId}
      attendeeNameByEmail={attendeeNameByEmail}
      attendeePhotoByEmail={attendeePhotoByEmail}
      attendeesLoading={attendeesLoading}
      className={className}
    />
  );
}
